import type { Network, FetchRequest, FetchResponse } from '@jet/environment';
import { fromEntries } from '@amp/web-apps-utils';
import { DEFAULT_STOREFRONT_CODE } from '~/constants/storefront';

const CORRELATION_KEY_HEADER = 'x-apple-jingle-correlation-key';
type FetchFunction = typeof window.fetch;

export type GetUserInfo = () => { mut: string; storefront: string } | undefined;
export interface UserCallbacks {
    getUserInfo: GetUserInfo;
}

export class Net implements Network {
    private readonly underlyingFetch: FetchFunction;
    private readonly _getUserInfo: GetUserInfo = () => null;

    constructor(underlyingFetch: FetchFunction, userCallbacks: UserCallbacks) {
        this.underlyingFetch = underlyingFetch;
        this._getUserInfo = userCallbacks?.getUserInfo ?? this._getUserInfo;
    }

    async fetch(request: FetchRequest): Promise<FetchResponse> {
        const requestStartTime = getTimestampMs();

        if (request.url.startsWith('/api/classical/')) {
            request.headers = request.headers || {};

            // TODO: rdar://78158575: handle authbridge, MUT cookie may not
            //       exist at this point but we also don't want to block on MusicKit
            const userInfo = this._getUserInfo();
            const mut = userInfo?.mut;

            // Edge Case where a Non Subscriber makes request to fetch content, we have to send this extra header 'x-is-auth' and remove MUT
            if (mut && !('x-is-auth' in request.headers)) {
                request.headers['media-user-token'] = mut;
            }

            const authStorefront =
                userInfo?.storefront || DEFAULT_STOREFRONT_CODE;
            if (authStorefront) {
                request.headers['auth-storefront'] =
                    authStorefront.toLowerCase();
            }
        }

        // TODO: rdar://78158575: timeout
        const response = await this.underlyingFetch(request.url, {
            // credentials: 'include',
            ...request,
        });

        const responseStartTime = getTimestampMs();

        const { ok, redirected, status, statusText, url } = response;

        const headers = fromEntries(response.headers);
        const body = await response.text();

        const responseEndTime = getTimestampMs();

        return {
            ok,
            headers,
            redirected,
            status,
            statusText,
            url,
            body,
            // TODO: rdar://78158575: redirect: 'manual' to get all metrics?
            metrics: [
                {
                    clientCorrelationKey: response.headers.get(
                        CORRELATION_KEY_HEADER,
                    ),
                    pageURL: response.url,
                    requestStartTime,
                    responseStartTime,
                    responseEndTime,
                    // TODO: rdar://78158575: responseWasCached?
                    // TODO: rdar://78158575: parseStartTime/parseEndTime
                },
            ],
        };
    }
}

/**
 * Returns the current UTC timestamp in milliseconds.
 */
function getTimestampMs(): number {
    return Date.now();
}
